import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { config, COUNTRY } from '@/config';
import * as Styles from './useCreditCardPayment.styles';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import {
  XRAY_ENABLED_FINGERPRINT,
  XRAY_INSTALLMENTS_MODAL,
  XRAY_VERIFY_CARD_CHECK,
  ENABLE_SAVE_CREDITCARD_ATTEMPT_JWT_TOKEN_PAAS,
  SEND_PROPERTIES_PROCESSOR_EBANX_PAAS,
} from '@/config/GrowthBook/constants';
import { CALLER, PaymentStatusTypes } from '../usePaymentModal/usePaymentModal.types';
import { loadInvoices, updateStatusInvoiceToConfirmingPayment } from '@/redux/actions/invoices';
import { getPaymentMethod } from '@/redux/actions/paymentMethod';
import { ERROR_CC_METHOD, RECEIVE_GATORPAY_PAAS_SAVE_CREDITCARD } from '@/redux/actions/actionsTypes';
import { enqueueSnackbar } from '@/redux/actions/notifications';
import { notifierError } from '@/utils/Application/notifier';
import { PHISHING_STATUS_ANALYSIS } from '@/config/invoices/invoiceStatus';
import { isBRServer } from '@/utils/Validators/validation';
import { getCreditCardAttempt, saveCreditCard, saveCreditCardAttempt } from '@/redux/actions/creditCardPaas';
import { billingActions } from '@/redux/modules';
import { generateRecaptchaToken, loadReCaptchaPaaS } from '@/utils/Application/reCaptcha';
import { Trans } from 'react-i18next';
import { useFingerprint } from '@/contexts/Fingerprint/useFingerprint';
import logger from '@/utils/logger';

const useCreditCardPayment = ({
  creditCardInstallments,
  setCcButtonEnabled,
  setCcButtonLoading,
  selectedInstallment,
  setPaymentStatus,
  useCardOnNextRenovation = false,
  handleSwitchPaymentMethod,
  invoiceId,
  caller,
  showIframeMiniCreditCard = true,
  setError,
  callFastCheckout,
}) => {
  const [heightIframe, setHeightIframe] = useState(260);
  const [canSubmit, setCanSubmit] = useState(false);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);

  const summaryState = useSelector(state => state.summary);
  const creditCardAttempt = useSelector(state => state.creditCardAttempt);
  const attemptToken = creditCardAttempt && creditCardAttempt.attemptToken;

  const enabledFingerprint = useFeatureIsOn(XRAY_ENABLED_FINGERPRINT);
  const isCreditCardInstallmentsEnabled = useFeatureIsOn(XRAY_INSTALLMENTS_MODAL);
  const isVerifyCardCheckActive = useFeatureIsOn(XRAY_VERIFY_CARD_CHECK);
  const isSaveCreditCardAttemptJwtTokenPaasEnabled = useFeatureIsOn(ENABLE_SAVE_CREDITCARD_ATTEMPT_JWT_TOKEN_PAAS);
  const sendPropertiesProcessorEbanxPaaS = useFeatureIsOn(SEND_PROPERTIES_PROCESSOR_EBANX_PAAS);
  const isFastCheckoutCaller = caller === CALLER.FASTCHECKOUT;
  const isSubscriptionsCaller = caller === CALLER.SUBSCRIPTIONS;
  const isInvoicesCaller = caller === CALLER.INVOICES;

  const iframeRef = useRef(null);
  const dispatch = useDispatch();

  const fingerprint = useFingerprint();

  const {
    IFRAME_URL,
    CLIENT_ID,
    IFRAME_PAGENAME,
    PROCESSOR,
  } = config.PAAS_CONFIG;

  const providerIdentifier = enabledFingerprint && `${COUNTRY}${summaryState && summaryState.id}${Date.now()}`;

  const onSetNewAlertError = useCallback((error) => {
    setError && setError(error);
  }, [setError]);

  const handleError = useCallback((error) => {
    if (isFastCheckoutCaller) {
      return onSetNewAlertError({ error: true, title: '', message: <Trans i18nKey={error} /> });
    }

    dispatch(enqueueSnackbar(error, notifierError));
  }, [onSetNewAlertError, dispatch, isFastCheckoutCaller]);

  const onSaveCreditCard = useCallback(() => {
    const hasSelectedInstallment = creditCardInstallments && creditCardInstallments.filter(installment => installment.gateway === selectedInstallment).length > 0;

    const enabledSubmit = !!selectedInstallment && !isCreditCardInstallmentsEnabled ? true : hasSelectedInstallment;
    setCanSubmit(enabledSubmit);
  }, [selectedInstallment, creditCardInstallments, isCreditCardInstallmentsEnabled]);

  const changePaymentMethod = useCallback(() => {
    setCcButtonLoading(true);
    dispatch(getPaymentMethod(selectedInstallment.value, invoiceId, providerIdentifier, useCardOnNextRenovation)).then((action) => {
      const isError = action.type === ERROR_CC_METHOD;

      setCcButtonLoading(false);

      if (isError) {
        switch (action.status) {
          case 'INVALID_INVOICE_STATUS':
            setPaymentStatus && setPaymentStatus(PaymentStatusTypes.FAILURE_CREDIT_CARD);
            updateStatusInvoiceToConfirmingPayment(invoiceId);
            break;
          case 'PENDING_ANALYSIS':
            loadInvoices('Unpaid', true);
            setPaymentStatus && setPaymentStatus(PaymentStatusTypes.SUCCESS_GENERIC);
            break;
          default:
            setPaymentStatus && setPaymentStatus(PaymentStatusTypes.FAILURE_CREDIT_CARD);
            if (Object.prototype.hasOwnProperty.call(action, 'key') && action.key === PHISHING_STATUS_ANALYSIS) {
              loadInvoices('Unpaid', true);
            }
            break;
        }
      }

      if (!isError) {
        setPaymentStatus && setPaymentStatus(PaymentStatusTypes.SUCCESS_GENERIC);
        loadInvoices('Unpaid', true);
        updateStatusInvoiceToConfirmingPayment(invoiceId);
      }
    });
  }, [
    dispatch,
    invoiceId,
    providerIdentifier,
    setPaymentStatus,
    useCardOnNextRenovation,
    selectedInstallment,
    setCcButtonLoading,
  ]);

  const sendCurrentCreditCard = useCallback(() => {
    if (isSubscriptionsCaller) {
      handleSwitchPaymentMethod();
    }

    if (isInvoicesCaller || isFastCheckoutCaller) {
      changePaymentMethod();
    }
  }, [changePaymentMethod, handleSwitchPaymentMethod, isFastCheckoutCaller, isSubscriptionsCaller, isInvoicesCaller]);

  const postMessage = useCallback((body) => {
    const { contentWindow } = document.getElementById('token-iframe');
    contentWindow.postMessage(JSON.stringify({
      securePayMessage: true,
      body,
    }), IFRAME_URL);
  }, [IFRAME_URL]);

  const callGetToken = useCallback((token) => {
    dispatch(getCreditCardAttempt(fingerprint, isSaveCreditCardAttemptJwtTokenPaasEnabled))
      .then((creditCardAttempt) => {
        const attemptToken = creditCardAttempt && creditCardAttempt.data && creditCardAttempt.data.attemptToken;
        const successAttemptToken = creditCardAttempt && creditCardAttempt.data && creditCardAttempt.data.success;

        if (creditCardAttempt !== undefined && !successAttemptToken) {
          dispatch(enqueueSnackbar('snackbar.creditCard.paymentFailByAttemptsLimit', notifierError));
          setCcButtonEnabled(true);
          setCcButtonLoading(false);
          return;
        }

        const argsPostMessage = [token, attemptToken];

        if (sendPropertiesProcessorEbanxPaaS && !isBRServer) {
          const processor = PROCESSOR && PROCESSOR.EBANX;
          argsPostMessage.push(processor);
        }

        postMessage({
          callFunction: {
            name: 'hgLatamGetToken',
            arguments: argsPostMessage,
          },
        });
      });
  }, [dispatch, fingerprint, postMessage, setCcButtonEnabled, setCcButtonLoading, isSaveCreditCardAttemptJwtTokenPaasEnabled, sendPropertiesProcessorEbanxPaaS, PROCESSOR]);

  const submitCreditCard = () => {
    setCcButtonEnabled(false);
    setCcButtonLoading(true);

    postMessage({
      callFunction: {
        name: 'hgLatamValidateAll',
        arguments: [],
      },
    });
  };

  const checkSubscriptionsToChangePaymentMethod = useCallback(() => {
    dispatch(billingActions.subscriptions.requestSubscriptionsNotInCreditCard());
  }, [dispatch]);

  const handleIframeLoad = useCallback(() => {
    setIsIframeLoaded(true);
  }, []);

  const onMessage = useCallback((event) => {
    if (event.data.securePayMessage !== true) {
      return false;
    }
    let { body } = event.data;
    if (body.constructor === String) {
      try {
        body = JSON.parse(body);
        if (Array.isArray(body)) {
          const hasError = body.some((item) => {
            const code = parseInt(item.code, 10);
            return code !== 200;
          });
          if (hasError) {
            logger && logger.error('Erro recebido do postToParent:', body);
            dispatch(enqueueSnackbar('snackbar.creditCard.paas.errorCreditCardAttempt', notifierError));
            setCcButtonEnabled(true);
            setCcButtonLoading(false);
            return;
          }
        }
      // eslint-disable-next-line no-console
      } catch (e) {
        logger && logger.error('Erro ao converter mensagem do iframe:', e);
        handleError('snackbar.creditCard.paas.errorCreditCardAttempt');
        setCcButtonEnabled(true);
        setCcButtonLoading(false);
        return;
      }
    }
    if (body.constructor === Object) {
      if (body.error) {
        logger && logger.error('Erro recebido do postToParent:', body.error);
        handleError('snackbar.creditCard.paas.errorCreditCardAttempt');
        setCcButtonEnabled(true);
        setCcButtonLoading(false);
        return;
      }

      if (body.isValid !== undefined && body.fields !== undefined) {
        if (!body.isValid) {
          handleError('snackbar.creditCard.paas.errorSaveCreditCard');
          setCcButtonEnabled(true);
          setCcButtonLoading(false);
          return;
        }
        generateRecaptchaToken(config.NEW_RECAPTCHA_PAAS, 'userexistis').then((token) => {
          callGetToken(token);
        }).catch(() => {
          handleError('snackbar.creditCard.paas.errorSaveCreditCard');
          setCcButtonEnabled(true);
          setCcButtonLoading(false);
        });
      }
      if (body.creditCard !== undefined && body.creditCard.data !== '') {
        body.creditCard.token = body.id;
        if (isVerifyCardCheckActive) {
          if (body.properties !== undefined && body.properties.Status !== undefined && body.properties.Status !== 1) {
            handleError('snackbar.creditCard.paas.errorVerifyCard');
            setPaymentStatus && setPaymentStatus(PaymentStatusTypes.FAILURE_CREDIT_CARD);
            setCcButtonEnabled(true);
            setCcButtonLoading(false);

            if (isBRServer || isSaveCreditCardAttemptJwtTokenPaasEnabled) {
              dispatch(saveCreditCardAttempt(body.creditCard.token, attemptToken));
            }
            return;
          }
        }
        setCcButtonLoading(true);
        setCcButtonEnabled(false);
        dispatch(saveCreditCard(body.creditCard)).then((data) => {
          onSaveCreditCard();
          checkSubscriptionsToChangePaymentMethod();

          if (isSubscriptionsCaller) {
            handleSwitchPaymentMethod();
          }

          if (isInvoicesCaller) {
            changePaymentMethod();
          }

          if (isFastCheckoutCaller) {
            callFastCheckout();
            setCcButtonLoading(false);
            setCcButtonEnabled(true);
          }

          if ((isBRServer || isSaveCreditCardAttemptJwtTokenPaasEnabled) && data.type === RECEIVE_GATORPAY_PAAS_SAVE_CREDITCARD) {
            dispatch(saveCreditCardAttempt(body.creditCard.token, attemptToken));
          }
        });
      }
      if (body.size !== undefined && body.size.height !== 0) {
        setHeightIframe(body.size.height);
      }
    }
  }, [
    isVerifyCardCheckActive,
    dispatch,
    checkSubscriptionsToChangePaymentMethod,
    setCcButtonEnabled,
    setPaymentStatus,
    setHeightIframe,
    callGetToken,
    onSaveCreditCard,
    changePaymentMethod,
    setCcButtonLoading,
    handleSwitchPaymentMethod,
    handleError,
    isFastCheckoutCaller,
    isSubscriptionsCaller,
    isInvoicesCaller,
    callFastCheckout,
    attemptToken,
    isSaveCreditCardAttemptJwtTokenPaasEnabled,
  ]);

  useEffect(() => {
    loadReCaptchaPaaS(true);
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [onMessage]);

  useEffect(() => {
    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [IFRAME_URL, onMessage]);

  const renderIframe = (
    <Styles.Iframe
      ref={iframeRef}
      height={`${heightIframe}px`}
      width="100%"
      src={`${IFRAME_URL}/payment/${IFRAME_PAGENAME}?${showIframeMiniCreditCard ? 'creditshow=show' : ''}&${isFastCheckoutCaller ? 'fastcheckout' : 'portal'}=show&clientId=${typeof CLIENT_ID === 'string'
        ? CLIENT_ID
        : CLIENT_ID[summaryState.personType.toLowerCase()]
      }&country=${COUNTRY}`}
      id="token-iframe"
      title="cc"
      isFastCheckoutCaller={isFastCheckoutCaller}
      onLoad={handleIframeLoad}
    />
  );

  return {
    canSubmit,
    submitCreditCard,
    sendCurrentCreditCard,
    renderIframe,
    isIframeLoaded,
  };
};

export default useCreditCardPayment;
